/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useEffect, useState } from 'react';

import {
  ExclamationTriangleIcon,
  InfoCircledIcon
} from '@radix-ui/react-icons';
import {
  Tooltip as ApsaraToolTip,
  EmptyState,
  Flex,
  IconButton,
  Table,
  Text
} from '@raystack/apsara';
import _ from 'lodash';
import { Bar, Cell } from 'recharts';

import BarGraph from '@src/components/Custom/BarGraph';
import { TileList } from '@src/components/PixxelUIKit/Skeleton';
import { For, Show } from '@src/hoc';
import { fetchTabData } from '@src/utils/helpers/WorkflowHelpers';

import styles from './styles.module.css';

export type OutputType = Record<string, any>;
type ListType = OutputType[];

interface DiscreteOutputTableProps {
  data: ListType;
  heading: string;
  wrapperClassName?: string;
}

interface DiscreteOutputCommonProps {
  outputs: ListType | OutputType;
  activeAoi: any;
  heading: string;
  graphOutputName: string;
  areaInfoMessage: string;
  wrapperClassName?: string;
  graphDataFormatter?: (data: OutputType[]) => OutputType[];
}

export const DiscreteOutputTable = ({
  data,
  heading,
  wrapperClassName
}: DiscreteOutputTableProps) => {
  return (
    <Fragment>
      <BarGraph
        data={data}
        wrapperClassName={wrapperClassName}
        width={400}
        height={350}
        axis={{
          x: {
            key: 'Class name',
            label: ''
          },
          y: {
            key: 'Area Percentage (%)',
            label: 'Area Percentage (%)',
            tickFormatter: value => `${value}%`
          }
        }}
      >
        <Bar dataKey='Area Percentage (%)'>
          {data.map((bar, index) => (
            <Cell key={index} fill={bar?.['Color codes'] as string} />
          ))}
        </Bar>
      </BarGraph>
      <div className={styles.discreteTableWrapper}>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head></Table.Head>
              <Table.Head>{heading}</Table.Head>
              <Table.Head>
                Area (km<sup>2</sup>)
              </Table.Head>
              <Table.Head>Area (%)</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <For each={data}>
              {(item, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <div
                      className={styles.filledBox}
                      style={{
                        backgroundColor: `${item?.['Color codes']}`
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell>{item['Class name']}</Table.Cell>
                  <Table.Cell>
                    {item['Class area in km2']} km<sup>2</sup>
                  </Table.Cell>
                  <Table.Cell>{item['Area Percentage (%)']} %</Table.Cell>
                </Table.Row>
              )}
            </For>
          </Table.Body>
        </Table>
      </div>
    </Fragment>
  );
};

export const DiscreteOutputCommon = ({
  outputs = [],
  activeAoi,
  heading = '',
  graphOutputName = '',
  graphDataFormatter,
  areaInfoMessage = '',
  wrapperClassName = 'bar-graph-wrapper'
}: DiscreteOutputCommonProps) => {
  const [tabData, setTabData] = useState<Record<string, ListType>>({});
  const [activeTabData, setActiveTabData] = useState<ListType>([]);
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (outputs) {
      fetchTabData(
        outputs,
        setTabData,
        (value: boolean) => setLoader(value),
        activeAoi
      );
    }
  }, [outputs, activeAoi]);

  // form graph data for recharts
  useEffect(() => {
    if (!_.isEmpty(tabData)) {
      const graphList = tabData[graphOutputName] || [];

      const graphData = graphDataFormatter
        ? graphDataFormatter(graphList)
        : graphList.map(item => ({
            ...item,
            'Class area in km2': Number(item['Class area in km2']),
            'Area Percentage (%)': Number(item['Percentage of total'])
          }));
      setActiveTabData(graphData);
    }
  }, [tabData, graphOutputName]);

  return (
    <>
      <Flex
        gap={9}
        direction='column'
        align='start'
        className={styles.lulcCommonContainer}
      >
        <Flex align='center' gap='small'>
          <Text size={4} weight={500} variant='primary'>
            Area Coverage
          </Text>
          <ApsaraToolTip message={areaInfoMessage} side='top'>
            <IconButton size={3} data-test-id='tree-species-output-info-btn'>
              <InfoCircledIcon />
            </IconButton>
          </ApsaraToolTip>
        </Flex>
        <Show when={!loader} fallback={<TileList />}>
          <Show
            when={activeTabData.length > 0}
            fallback={
              <EmptyState
                icon={<ExclamationTriangleIcon />}
                heading='Failed to generate graph!'
                subHeading='Model failed to generate graph for this insight! Please try again.'
              />
            }
          >
            <DiscreteOutputTable
              data={activeTabData.slice(0, -1)}
              heading={heading}
              wrapperClassName={wrapperClassName}
            />
          </Show>
        </Show>
      </Flex>
    </>
  );
};

DiscreteOutputCommon.displayName = 'DiscreteOutputCommon';
